/**
 * @fileoverview Member Opportunities Management API Module
 * This module provides API functions for managing healthcare opportunities for members including:
 * - Fetching member-specific opportunities with search and pagination
 * - Retrieving prescription (Rx) history for specific drugs and members
 * - Editing and updating prescription information and opportunities
 * - Opportunity-based care management and intervention tracking
 * - Search functionality across opportunity types and descriptions
 */

import api from "./api";

// A member with no HALO program is addressed by a synthetic
// "{subscriberNum}-{personCode}" id; there is nothing to fetch for them.
const hasNoRealMemberId = (memberId) => String(memberId).includes("-");

export const fetchOpportunities = async (memberId, searchTerm, page, size) => {
  if (hasNoRealMemberId(memberId)) {
    return { content: [], totalElements: 0, totalPages: 0 };
  }
  try {
    const params = new URLSearchParams();
    const hasSearch = searchTerm && searchTerm.trim() !== "";
    if (!hasSearch && size !== undefined && size !== null) {
      params.append("size", size);
    }
    if (!hasSearch && page !== undefined && page !== null) {
      params.append("page", page);
    }
    if (searchTerm) {
      params.append("search", searchTerm);
    }
    const response = await api.get(
      `/outreach/members/${memberId}/opportunities?${params.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    );

    return {
      content: response.data.content || [],
      totalElements: response.data.totalElements || 0,
      totalPages: response.data.totalPages || 0,
    };
  } catch (e) {
    return `fetchOpportunities failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};

export const fetchRxHistory = async (memberId, drugId) => {
  try {
    const response = await api.get(
      `/outreach/members/${memberId}/opportunities/rx-history`,
      {
        params: { drugId },
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    );
    return response.data;
  } catch (e) {
    return `fetchRxHistory failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};

export const editRx = async (memberId, payload) => {
  try {
    const response = await api.put(
      `/outreach/members/${memberId}/opportunities/rx-history`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    );
    return response.data;
  } catch (e) {
    return `editRx failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};

export const fetchPharmacyOpportunities = async (pharmacyId, memberId) => {
  try {
    const response = await api.get(
      `/outreach/pharmacies/${pharmacyId}/opportunities`,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    );
    return response.data;
  } catch (e) {
    return `fetchPharmacyOpportunities failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};

export const fetchProviderOpportunities = async (providerId, memberId) => {
  try {
    const response = await api.get(
      `/outreach/providers/${providerId}/opportunities`,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    );
    return response.data;
  } catch (e) {
    return `fetchProviderOpportunities failed: ${e instanceof Error ? e.message : String(e)}`;
  }
};

export const updatePapSavings = async (
  memberId,
  opportunityId,
  planSavings,
  memberSavings,
) => {
  const response = await api.post(
    `/outreach/members/${memberId}/opportunities/${opportunityId}/program-details`,
    {
      planSavings,
      memberSavings,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    },
  );
  return response.data;
};

export const updateProgramDetails = async (
  memberId,
  opportunityId,
  planSavings,
  memberSavings,
  perFillAmount,
) => {
  const response = await api.post(
    `/outreach/members/${memberId}/opportunities/${opportunityId}/program-details`,
    {
      planSavings,
      memberSavings,
      perFillAmount,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    },
  );
  return response.data;
};

const memberClinicalProgramsInFlight = new Map();
const memberClinicalProgramsCache = new Map();

export const fetchMemberClinicalPrograms = async (
  memberId,
  forceRefresh = false,
) => {
  if (!memberId || hasNoRealMemberId(memberId)) return [];
  const cacheKey = String(memberId);

  if (!forceRefresh && memberClinicalProgramsCache.has(cacheKey)) {
    return memberClinicalProgramsCache.get(cacheKey);
  }

  if (memberClinicalProgramsInFlight.has(cacheKey)) {
    return memberClinicalProgramsInFlight.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const response = await api.get(
        `/outreach/members/${memberId}/opportunities/clinical-programs`,
        {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        },
      );
      memberClinicalProgramsCache.set(cacheKey, response.data);
      // Auto-expire after 10 seconds so dynamic changes can be refreshed
      setTimeout(() => {
        memberClinicalProgramsCache.delete(cacheKey);
      }, 10000);
      return response.data;
    } catch (e) {
      return e;
    } finally {
      memberClinicalProgramsInFlight.delete(cacheKey);
    }
  })();

  memberClinicalProgramsInFlight.set(cacheKey, promise);
  return promise;
};

export const clearMemberClinicalProgramsCache = (memberId) => {
  if (memberId) {
    memberClinicalProgramsCache.delete(String(memberId));
    memberClinicalProgramsInFlight.delete(String(memberId));
  } else {
    memberClinicalProgramsCache.clear();
    memberClinicalProgramsInFlight.clear();
  }
};
